import { Component, OnInit } from '@angular/core';
import { Feature, FloorPlanService } from '../service/floor-plan.service';
import { Sensor } from '../models/sensor.model';
import { SensorsFilter, SensorsService } from '../service/sensors.service';
import { PricesService } from '../service/prices.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-floor-plan',
    templateUrl: './floor-plan.component.html',
    styleUrls: ['./floor-plan.component.scss'],
    standalone: false
})
export class FloorPlanComponent implements OnInit {
  rooms: Feature[] = [];
  sensorsById: Record<number, Sensor> = {};
  loading = false;
  private readonly filter: SensorsFilter = new SensorsFilter(0, 100);
  private readonly bounds = {
    minX: -130,
    maxX: 130,
    minY: -80,
    maxY: 80,
  };

  constructor(
    service: FloorPlanService,
    private sensorsService: SensorsService,
    public priceService: PricesService,
    private router: Router,
  ) {
    this.rooms = service.getRoomsData().features;
  }

  ngOnInit(): void {
    this.fetchSensors();
  }

  getRoomStyle(room: Feature): Record<string, string> {
    const coordinates = room.geometry?.coordinates?.[0] ?? [];
    if (!coordinates.length) {
      return {};
    }

    const xValues = coordinates.map(([x]) => x);
    const yValues = coordinates.map(([, y]) => y);
    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);

    const totalWidth = this.bounds.maxX - this.bounds.minX;
    const totalHeight = this.bounds.maxY - this.bounds.minY;

    const left = ((minX - this.bounds.minX) / totalWidth) * 100;
    const width = ((maxX - minX) / totalWidth) * 100;
    const top = ((this.bounds.maxY - maxY) / totalHeight) * 100;
    const height = ((maxY - minY) / totalHeight) * 100;

    return {
      left: `${left}%`,
      top: `${top}%`,
      width: `${width}%`,
      height: `${height}%`,
    };
  }

  getRoomSensor(room: Feature): Sensor | undefined {
    const sensorId = room.properties?.square;
    return sensorId ? this.sensorsById[sensorId] : undefined;
  }

  getBatteryPercent(sensor?: Sensor): number | null {
    if (!sensor) {
      return null;
    }

    const percentage = this.priceService.round((sensor.battery / 3) * 100, 2);
    return percentage > 100 ? 100 : percentage;
  }

  getBatteryStatusClass(sensor?: Sensor): string {
    if (!sensor) {
      return '';
    }

    return sensor.battery <= 2 ? 'batteryLow' : 'batteryOk';
  }

  openSensor(room: Feature): void {
    const sensorId = room.properties?.square;
    if (!sensorId) {
      return;
    }

    this.router.navigate(['/sensor', sensorId]);
  }

  private fetchSensors(): void {
    this.loading = true;
    this.sensorsService.find(this.filter).subscribe({
      next: data => {
        const mapped = (data?.values ?? []).reduce<Record<number, Sensor>>((acc, sensor) => {
          acc[sensor.sensorId] = sensor;
          return acc;
        }, {});
        this.sensorsById = mapped;
        this.loading = false;
      },
      error: () => {
        this.sensorsById = {};
        this.loading = false;
      }
    });
  }
}
